/**
 * Recipient-row building for the Purringer wizard (allVisitsTable.js, step 2).
 *
 * Builds one row per loaded sale with contact info, rest amount, overdue days
 * and last-reminder date, plus the default send-selection:
 *   selected = reachable (email or cellphone)
 *            AND not reminded within the last 7 days
 *            AND rest amount > 0.5 kr
 *
 * The step-2 list can be re-filtered (search) while the wizard is open, so the
 * rows are rebuilt from the reloaded sales. Rows that were already listed keep
 * the admin's manual (de)selection; only NEW rows get the default selection.
 *
 * @param {object} params
 * @param {Array} params.sales - saleStore.sales (already filtered to overdue invoices).
 * @param {Array} params.allPayments - saleStore.allPayments.
 * @param {function} params.sumTotal - (products, format) => number, from saleStore.
 * @param {function} params.sumRestPayments - (payments, total, format, hideEmpty) => number, from saleStore.
 * @param {Array} [params.prevRows] - The rows currently shown (before the rebuild).
 * @param {object} [params.prevSelected] - The current selection map { [saleId]: bool }.
 * @param {Date} [params.now] - Injectable clock for tests.
 * @returns {{ reminderRows: Array, reminderSelected: object }}
 */
export function buildReminderRows({
    sales = [],
    allPayments = [],
    sumTotal,
    sumRestPayments,
    prevRows = [],
    prevSelected = {},
    now = new Date(),
}) {
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const prevIds = new Set(prevRows.map(row => row.sale.id));

    const reminderRows = sales.map(sale => {
        const visitor = (sale.visitors || [])[0];
        // Dedicated invoice address wins over the general e-mail (mirrors the backend send routes).
        const email = (visitor && (visitor.invoiceEmail || visitor.email)) || '';
        const cellphone = (visitor && visitor.cellphone) || '';
        const salePayments = allPayments.filter(p => p.sale === sale.id);
        const productTotal = sumTotal(sale.products, false);
        const restAmount = sumRestPayments(salePayments, productTotal, false, false);
        const reminderDates = (sale.reminders || [])
            .map(r => new Date(r.date))
            .filter(d => !isNaN(d.getTime()));
        const lastReminder = reminderDates.length > 0
            ? new Date(Math.max(...reminderDates.map(d => d.getTime())))
            : null;
        const daysOverdue = sale.dueDate
            ? Math.floor((now.getTime() - new Date(sale.dueDate).getTime()) / (24 * 60 * 60 * 1000))
            : 0;
        return {
            sale,
            visitor,
            email,
            cellphone,
            restAmount,
            lastReminder,
            daysOverdue,
            reachable: !!(email || cellphone),
        };
    });

    const reminderSelected = {};
    reminderRows.forEach(row => {
        if (prevIds.has(row.sale.id)) {
            // A filter change must not nuke manual choices for rows that stay listed.
            reminderSelected[row.sale.id] = !!prevSelected[row.sale.id] && row.reachable;
            return;
        }
        const recentlyReminded = row.lastReminder && row.lastReminder > sevenDaysAgo;
        // Zero-rest sales (fully covered by registered payments but not yet marked
        // paid) must not be reminded by default — the admin can still re-check them.
        const hasRestAmount = row.restAmount > 0.5;
        reminderSelected[row.sale.id] = row.reachable && !recentlyReminded && hasRestAmount;
    });

    return { reminderRows, reminderSelected };
}

export default buildReminderRows;
